import { useEffect, useState } from "react";
import { ArrowLeftRight, Building2, Compass, Hammer, Share2, Ship, Swords, TriangleAlert, UserPlus, X } from "lucide-react";
import { useRect } from "@/city3d/Tour";
import { Btn, PANEL } from "./kit";
import { ACC, dur, fmt, today, useNow } from "./util";

const KEY = "xf_guide";
const SHARED = "xf_guide_shared";
const LATER = 30 * 60000;

// The guide is on until the player turns it off, on this device.
export function useGuide() {
  const [on, setOn] = useState(() => localStorage.getItem(KEY) !== "off");
  const set = (v) => { localStorage.setItem(KEY, v ? "on" : "off"); setOn(v); };
  return [on, set];
}

const TIPS = [
  "Tap any house to see who lives there, trade with them or challenge them to a duel.",
  "The ten biggest earners of the week move into Millionaires' Row every Monday.",
  "Your warehouse fills while you're away. Send it by train and it comes back worth half as much again.",
  "Docks you trade with earn more, and so do you when they trade back.",
  "Put your picture on a billboard, or a sign over your house, from the Advertise section.",
  "The mayor takes a tax on every ship, passes decrees and hands out grants from the city treasury.",
];

// The most useful thing to do right now, first match wins: { id, acc, icon, title, text, cta, open?, target?, run? }.
// `open` is the panel to open, `target` the button in it to ring, `run` does the thing outright.
function nextStep(e, me, now, act) {
  const ready = e.berths.filter((b) => b.ship && Date.parse(b.done) <= now);
  const idle = e.berths.filter((b) => !b.ship).length;
  const pay = ready.reduce((s, b) => s + b.pay, 0);
  const can = (cost) => cost != null && e.uxd >= cost;
  const deeper = e.ships.filter((s) => s.dredge > e.dredge).sort((a, b) => a.dredge - b.dredge)[0];
  const up = can(e.costs.berth) ? ["up-berth", "Build another berth", `One more ship at sea at a time, for ${fmt(e.costs.berth)} UXD.`]
    : deeper && can(e.costs.dredge) ? ["up-dredge", "Dredge deeper", `Deeper water lets the ${deeper.name.toLowerCase()} in, and it pays more a trip. ${fmt(e.costs.dredge)} UXD.`]
    : !e.warehouse.level && can(e.costs.warehouse) ? ["up-warehouse", "Build a warehouse", `It earns while you're away. ${fmt(e.costs.warehouse)} UXD.`] : null;
  return [
    e.rent?.short && { id: "rent", acc: "red", icon: TriangleAlert, title: `Rent is due ${Date.parse(e.rent.due) > now ? `in ${dur(Date.parse(e.rent.due) - now)}` : "now"}`,
      text: `You're ${fmt(e.rent.price - e.uxd)} UXD short. Collect ships and trade, or you move back to the Docks.`, cta: "See my rent", open: ["economy", "uptown"], target: "uptown-lot" },
    ready.length > 0 && { id: "collect", acc: "blue", icon: Ship, title: ready.length > 1 ? `${ready.length} ships are in` : "Your ship is in",
      text: `A ship left waiting over ${e.late_after_h} hours pays half, so collect now.`, cta: `Collect +${fmt(pay)}`, run: act.collect },
    idle > 0 && { id: "ship", acc: "blue", icon: Ship, title: "Send a ship", text: `${idle > 1 ? `${idle} berths are` : "A berth is"} empty. Bigger boats pay more but take longer.`,
      cta: "Show me", open: ["economy", "dock"], target: "start-ship" },
    e.duels.inbox > 0 && { id: "challenge", acc: "rose", icon: Swords, title: e.duels.inbox > 1 ? `${e.duels.inbox} duel challenges are waiting` : "You've been challenged to a duel",
      text: "Tap Fight to play 30 seconds of Harbour Rush for the pot, or X to turn it down.", cta: "Show me", open: ["economy", "duels"], target: "duel-accept" },
    !e.uptown && e.offer && e.uxd >= e.offer.price && { id: "uptown", acc: "green", icon: Building2, title: "You can move uptown",
      text: `A week in a house uptown costs ${fmt(e.offer.price)} UXD, and you have it. Your ships keep working.`, cta: "Show me", open: ["economy", "uptown"], target: "uptown-rent" },
    e.duels.free && { id: "duel", acc: "rose", icon: Swords, title: "Play your free duel",
      text: "One free duel a day: tap the ships before they sail off for 30 seconds and win UXD.", cta: "Show me", open: ["economy", "duels"], target: "quick-free" },
    e.visits.left > 0 && e.visits.suggest.length > 0 && { id: "trade", acc: "green", icon: ArrowLeftRight,
      title: "Trade with a neighbour", text: `${e.visits.left} trades left today. Each opens a mystery crate for you and boosts their ships.`,
      cta: "Show me", target: "action-visit", run: act.trade },
    me.invites_left > 0 && { id: "invite", acc: "grey", icon: UserPlus, title: "Invite your people",
      text: `Save a house next to yours for up to ${me.invites_left} friends. It waits, dark with their face on it, until they move in.`, cta: "Pick friends", run: act.invite },
    up && { id: up[0], acc: "blue", icon: Hammer, title: up[1], text: up[2], cta: "Show me", open: ["shop", "upgrades"], target: up[0] },
    localStorage.getItem(SHARED) !== today() && { id: "share", acc: "blue", icon: Share2, title: "Post your card on X",
      text: "Friends who tap it land at your house, and every trade they make boosts your ships.", cta: "Post it", run: act.share },
  ];
}

const waiting = (e, now) => {
  const sailing = e.berths.filter((b) => b.ship && Date.parse(b.done) > now).map((b) => Date.parse(b.done));
  return { id: "wait", acc: "blue", icon: Compass, title: sailing.length ? `Next ship docks in ${dur(Math.min(...sailing) - now)}` : "All caught up",
    text: TIPS[Math.floor(now / 60000) % TIPS.length], cta: "My dock", open: ["economy", "dock"] };
};

function Spotlight({ id, acc }) {
  const r = useRect(id);
  if (!r) return null;
  return <div className="pointer-events-none fixed z-[60] animate-pulse rounded-[20px]" data-testid="guide-ring"
    style={{ left: r.left - 6, top: r.top - 6, width: r.width + 12, height: r.height + 12, boxShadow: `0 0 0 3px ${ACC[acc]}, 0 0 28px 6px ${ACC[acc]}` }} />;
}

// The always-on coach over the action dock: one next step at a time, with why and how, and a button that does it or
// shows where. It moves on by itself as things change. "Not now" puts a step off for half an hour; X turns it off.
export function Guide({ econ, me, clockOffset, hidden, className = "", onOpen, onCollect, onInvite, onShare, onOff }) {
  const now = useNow(clockOffset);
  const [later, setLater] = useState({});
  const [spot, setSpot] = useState(null);
  useEffect(() => {
    if (!spot) return;
    const t = setTimeout(() => setSpot(null), 5000);
    return () => clearTimeout(t);
  }, [spot]);
  const act = {
    collect: () => onCollect(),
    trade: () => document.querySelector('[data-testid="action-visit"]')?.click(),
    invite: onInvite,
    share: () => { localStorage.setItem(SHARED, today()); onShare(); },
  };
  const s = nextStep(econ, me, now, act).find((x) => x && !(later[x.id] > now)) || waiting(econ, now);
  const go = () => {
    if (s.open) onOpen(...s.open);
    s.run?.();
    if (s.target) setSpot({ id: s.target, acc: s.acc, t: Date.now() });
  };

  return (
    <>
      {spot && <Spotlight key={spot.t} id={spot.id} acc={spot.acc} />}
      {!hidden && (
        <div className={`pointer-events-none absolute inset-x-0 z-10 flex justify-center px-2 ${className}`}
          style={{ bottom: "calc(3rem + 86px + env(safe-area-inset-bottom))" }}>
          <div key={s.id} className={`${PANEL} pop pointer-events-auto flex w-[min(100%,420px)] items-start gap-3 rounded-[20px] p-3`}
            style={{ "--acc": ACC[s.acc] }} data-testid="guide" data-step={s.id}>
            <span className="acc-fill grid h-10 w-10 shrink-0 place-items-center rounded-full"><s.icon className="h-5 w-5" strokeWidth={2.2} /></span>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-x-mute">Next up</div>
              <h2 className="text-[15px] font-semibold leading-tight" data-testid="guide-title">{s.title}</h2>
              <p className="mt-0.5 text-[13px] leading-snug text-x-mute" data-testid="guide-text">{s.text}</p>
              <div className="mt-2 flex items-center gap-3">
                <Btn onClick={go} data-testid="guide-go">{s.cta}</Btn>
                {s.id !== "wait" && (
                  <button type="button" onClick={() => setLater({ ...later, [s.id]: now + LATER })} data-testid="guide-later"
                    className="text-xs font-semibold text-x-mute transition hover:text-x-ink">Not now</button>
                )}
              </div>
            </div>
            <button type="button" onClick={onOff} aria-label="Turn the guide off" title="Turn the guide off" data-testid="guide-off"
              className="-m-1 grid h-7 w-7 shrink-0 place-items-center rounded-full text-x-mute transition hover:bg-x-raised hover:text-x-ink">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
